import type { Person } from "components/common/PersonPicker";
import { formatShortDayMonth } from "@/lib/dates";

// What the org's usage breakdowns share: the billing period they read, and
// the member a row is, found among the top spenders or by search.

export const DAY_SECONDS = 86_400;

export const utcDay = (unixSeconds: number) =>
  new Date(unixSeconds * 1000).toISOString().slice(0, 10);

/// A `YYYY-MM-DD` is a UTC day; a local Date built from its parts names the same day.
export const dayLabel = (day: string) => {
  const [y, m, d] = day.split("-").map(Number);
  return formatShortDayMonth(new Date(y, m - 1, d));
};

/** "16 Oct – 15 Nov"; the end is exclusive. */
export const periodRange = (startSeconds: number, endSeconds: number) =>
  `${dayLabel(utcDay(startSeconds))} – ${dayLabel(utcDay(endSeconds - DAY_SECONDS))}`;

export interface UsageMember {
  clientId: string;
  label: string;
  person: Person;
}

/** The picker searches by address, so anyone without one (a former member)
 *  is listed under their label and a short id. */
export function usageMember(
  clientId: string,
  name: string | null | undefined,
  email: string | null | undefined,
): UsageMember {
  const label = name || email || "Former member";
  return {
    clientId,
    label,
    person: email
      ? { email, displayName: name || null }
      : { email: `${label} · ${clientId.slice(-6)}` },
  };
}

/** The first `n` of `ranked` that `count`, plus the one picked out wherever it
 *  ranks; `rest` is everyone else who counts. */
export function splitTop<T>(
  ranked: T[],
  n: number,
  selected: T | null,
  count: (t: T) => boolean = () => true,
): { shown: T[]; rest: T[] } {
  const counted = ranked.filter(count);
  const shown = counted.slice(0, n);
  if (selected && !shown.includes(selected)) shown.push(selected);
  return { shown, rest: counted.filter((t) => !shown.includes(t)) };
}
